import { useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

type EventType =
  | 'page_view'
  | 'upload'
  | 'compress'
  | 'download'
  | 'view_pricing'
  | 'click_subscribe'
  | 'reach_limit'
  | 'register'
  | 'uploader_visible'
  | 'upload_click'
  | 'upload_success'
  | 'compression_start'
  | 'compression_success'
  | 'download_click'
  | 'compress_again_click'
  | 'example_image_click'
  | 'compression_auto_start'
  | 'scroll_50'
  | 'scroll_75'
  | 'scroll_100'
  | 'time_to_upload'
  | 'tool_start'
  | 'tool_success'
  | 'tool_interaction'
  | 'download_success'
  | 'crop_apply'
  | 'rotate_apply'
  | 'batch_start'
  | 'batch_complete'
  | 'batch_download_zip'
  | 'adjust_apply'
  | 'pdf_generate'
  | 'advanced_convert'
  | 'auto_process_triggered';

function getSessionId(): string {
  let id = sessionStorage.getItem('analytics_session_id');
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem('analytics_session_id', id);
  }
  return id;
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
  };
}

export function useAnalytics() {
  const sessionRef = useRef<{
    sessionId: string;
    referrer: string | undefined;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    device_type: string;
    pageLoadTime: number;
  } | null>(null);

  // Initialize session data once
  if (!sessionRef.current) {
    const utm = getUtmParams();
    sessionRef.current = {
      sessionId: getSessionId(),
      referrer: document.referrer || undefined,
      ...utm,
      device_type: getDeviceType(),
      pageLoadTime: Date.now(),
    };
  }

  const trackEvent = useCallback(
    (eventType: EventType, eventData?: Record<string, unknown>) => {
      // No trackear en la página de analytics
      if (window.location.pathname === '/analytics') return;

      // No trackear en entorno Lovable
      const host = window.location.hostname;
      if (host.includes('lovable.dev') || host.includes('id-preview--')) return;

      const s = sessionRef.current!;

      // Enrich upload_success with time_to_upload_ms
      let enrichedData = eventData || {};
      if (eventType === 'upload_success' && s.pageLoadTime) {
        enrichedData = {
          ...enrichedData,
          time_to_upload_ms: Date.now() - s.pageLoadTime,
        };
      }

      // Fire-and-forget
      supabase.functions
        .invoke('track-event', {
          body: {
            session_id: s.sessionId,
            event_type: eventType,
            event_data: enrichedData,
            referrer: s.referrer,
            utm_source: s.utm_source,
            utm_medium: s.utm_medium,
            utm_campaign: s.utm_campaign,
            device_type: s.device_type,
          },
        })
        .catch(() => {
          // Silent fail - analytics should never break the app
        });
    },
    []
  );

  const trackPageView = useCallback(
    (page: string) => {
      trackEvent('page_view', { page });
    },
    [trackEvent]
  );

  return { trackEvent, trackPageView };
}
